import * as THREE from "three";
import type { DecorModel } from "../../core/decorModels";

type DecorCoat = NonNullable<DecorModel["coat"]>;

interface CoatRecipe {
  /** Couleurs peintes (`rrggbb`, telles qu'enregistrées dans le modèle) que le pelage remplace. */
  painted: string[];
  /** Sur la tête, la peinture de ces couleurs n'est remplacée qu'en arrière du museau. */
  headPainted?: string[];
  /** Groupes d'os dont la peinture reste telle quelle : la queue, les sabots. */
  kept: RegExp;
  light: string;
  dark: string;
  /** Corps GLSL de `coatPattern(vec3 p, vec4 region)` : 0 pour `light`, 1 pour `dark`. `p` est en mètres, y vers le haut, l'avant vers -z. */
  pattern: string;
}

/** Rayures orientées selon la partie du corps ; l'orientation tourne d'une partie à l'autre, les rayures ne se croisent jamais. */
const stripes = (frequencies: [number, number, number, number], threshold: string) => /* glsl */ `
  float forward = -p.z;
  float up = p.y;
  vec4 phases = vec4(
    (forward + up * 0.3) * ${frequencies[0].toFixed(1)} + 0.9 * sin(up * 7.0),
    up * ${frequencies[1].toFixed(1)},
    (forward * 0.7 + up * 0.7) * ${frequencies[2].toFixed(1)},
    (forward * 0.35 + up) * ${frequencies[3].toFixed(1)}
  );
  return coatBand(dot(region, phases) / max(dot(region, vec4(1.0)), 0.0001), ${threshold});
`;

const SPOTS = /* glsl */ `
  float size = mix(0.3, 0.16, region.y) * mix(1.0, 0.8, region.z);
  vec3 q = p / size;
  vec3 base = floor(q);
  float nearest = 8.0;
  float second = 8.0;
  vec3 owner = base;
  for (int x = -1; x <= 1; x++)
    for (int y = -1; y <= 1; y++)
      for (int z = -1; z <= 1; z++) {
        vec3 cell = base + vec3(float(x), float(y), float(z));
        vec3 seed = cell + 0.15 + 0.7 * coatHash(cell);
        float reach = length(seed - q);
        if (reach < nearest) {
          second = nearest;
          nearest = reach;
          owner = cell;
        } else if (reach < second) {
          second = reach;
        }
      }
  float gap = second - nearest;
  float blur = fwidth(gap) + 0.0001;
  float inside = smoothstep(0.1 - blur, 0.1 + blur, gap);
  return inside * (0.82 + 0.18 * coatHash(owner + 17.0).x);
`;

const RECIPES: Record<DecorCoat, CoatRecipe> = {
  zebra: { painted: ["e2ded2", "030303"], headPainted: ["060505"], kept: /^Tail/, light: "#fbfaf5", dark: "#1d1b1a", pattern: stripes([22, 34, 28, 44], "0.05") },
  giraffe: { painted: ["611c05", "862d09", "7a2406", "d2b174"], kept: /^(Tail|Head|Ear)/, light: "#e6cf9c", dark: "#8a4416", pattern: SPOTS },
  // Des rayures fines et rompues, épaisses sur l'échine, qui s'effilent vers le ventre.
  tiger: { painted: ["af2d06", "030201"], kept: /^Ear/, light: "#d8742a", dark: "#1b130d", pattern: stripes([30, 40, 34, 52], "mix(0.92, 0.42, smoothstep(0.45, 0.95, p.y)) + 0.14 * sin(-p.z * 9.0 + p.x * 6.0 + p.y * 4.0)") },
};

/** Part de la longueur de la tête, depuis l'avant, qui garde sa couleur : le museau. */
const MUZZLE_SHARE = 0.38;

const COAT_VERTEX_HEAD = /* glsl */ `
  attribute vec3 coatPosition;
  attribute vec4 coatRegion;
  varying vec3 vCoatPosition;
  varying vec4 vCoatRegion;
`;

const COAT_VERTEX_BODY = /* glsl */ `
  #include <begin_vertex>
  vCoatPosition = coatPosition;
  vCoatRegion = coatRegion;
`;

const COAT_FRAGMENT_HEAD = /* glsl */ `
  uniform vec3 uCoatLight;
  uniform vec3 uCoatDark;
  varying vec3 vCoatPosition;
  varying vec4 vCoatRegion;

  float coatBand(float phase, float threshold) {
    float wave = sin(phase);
    float blur = fwidth(wave) * 0.75 + 0.0001;
    return smoothstep(threshold - blur, threshold + blur, wave);
  }

  vec3 coatHash(vec3 cell) {
    cell = vec3(dot(cell, vec3(127.1, 311.7, 74.7)), dot(cell, vec3(269.5, 183.3, 246.1)), dot(cell, vec3(113.5, 271.9, 124.6)));
    return fract(sin(cell) * 43758.5453);
  }

`;

const COAT_FRAGMENT_BODY = /* glsl */ `
  #include <color_fragment>
  float coatMask = vCoatRegion.x + vCoatRegion.y + vCoatRegion.z + vCoatRegion.w;
  if (coatMask > 0.001) {
    float coatDark = coatPattern(vCoatPosition, vCoatRegion);
    diffuseColor.rgb = mix(diffuseColor.rgb, mix(uCoatLight, uCoatDark, coatDark), min(1.0, coatMask));
  }
`;

const REGION_OF_BONE: [RegExp, number][] = [
  [/UpperLeg|LowerLeg/, 1],
  [/Neck/, 2],
  [/Head|Ear/, 3],
  [/Tail/, 1],
];

/**
 * Remplace la peinture facette par facette d'un pelage (rayures, taches) par un motif dessiné pixel
 * par pixel sur la forme de repos de l'animal : ses lignes traversent les facettes et suivent les os.
 */
export function prepareCoat(root: THREE.Object3D, coat: DecorCoat): void {
  const recipe = RECIPES[coat];
  root.updateMatrixWorld(true);
  const inverseRoot = root.matrixWorld.clone().invert();
  root.traverse((node) => {
    if (!(node instanceof THREE.SkinnedMesh)) return;
    const geometry = node.geometry as THREE.BufferGeometry;
    const position = geometry.getAttribute("position");
    const color = geometry.getAttribute("color");
    const joints = geometry.getAttribute("skinIndex");
    const weights = geometry.getAttribute("skinWeight");
    if (geometry.index || !position || !color || !joints || !weights) return;
    const toRoot = new THREE.Matrix4().multiplyMatrices(inverseRoot, node.matrixWorld);
    const bones = node.skeleton.bones.map((bone) => bone.name);
    const regions = new Float32Array(position.count * 4);
    const hex = (index: number) => [color.getX(index), color.getY(index), color.getZ(index)].map((channel) => Math.round(channel * 255).toString(16).padStart(2, "0")).join("");
    const strongest = (index: number) => {
      let best = 0;
      for (let k = 1; k < 4; k++) if (weights.getComponent(index, k) > weights.getComponent(index, best)) best = k;
      return bones[joints.getComponent(index, best)] ?? "";
    };
    const head = headSpan(position, toRoot, (index) => /Head/.test(strongest(index)));
    const point = new THREE.Vector3();
    for (let index = 0; index < position.count; index++) {
      const face = index - (index % 3);
      const painted = hex(face);
      if (recipe.kept.test(strongest(face))) continue;
      const headPaint = recipe.headPainted?.includes(painted) && /Head/.test(strongest(face));
      if (headPaint) {
        point.fromBufferAttribute(position, face).applyMatrix4(toRoot);
        if (!head || -point.z > head.front - (head.front - head.back) * MUZZLE_SHARE) continue;
      } else if (!recipe.painted.includes(painted)) continue;
      for (let k = 0; k < 4; k++) {
        const weight = weights.getComponent(index, k);
        const bone = bones[joints.getComponent(index, k)] ?? "";
        const region = REGION_OF_BONE.find(([pattern]) => pattern.test(bone))?.[1] ?? 0;
        regions[index * 4 + region] += weight;
      }
    }
    geometry.setAttribute("coatRegion", new THREE.BufferAttribute(regions, 4));
    // La position du sommet habillé par le squelette au repos, en mètres : la pose de repos des os ne coïncide pas toujours avec celle du maillage.
    const rest = new Float32Array(position.count * 3);
    const restPoint = new THREE.Vector3();
    for (let index = 0; index < position.count; index++) node.getVertexPosition(index, restPoint).applyMatrix4(toRoot).toArray(rest, index * 3);
    geometry.setAttribute("coatPosition", new THREE.BufferAttribute(rest, 3));
    const materials: THREE.Material[] = Array.isArray(node.material) ? node.material : [node.material];
    for (const material of materials) coatMaterial(material, coat);
  });
}

function headSpan(position: THREE.BufferAttribute | THREE.InterleavedBufferAttribute, toRoot: THREE.Matrix4, isHead: (index: number) => boolean): { front: number; back: number } | null {
  const point = new THREE.Vector3();
  let front = -Infinity;
  let back = Infinity;
  for (let index = 0; index < position.count; index++) {
    if (!isHead(index)) continue;
    const forward = -point.fromBufferAttribute(position, index).applyMatrix4(toRoot).z;
    front = Math.max(front, forward);
    back = Math.min(back, forward);
  }
  return front > back ? { front, back } : null;
}

function coatMaterial(material: THREE.Material, coat: DecorCoat): void {
  if (material.userData.coat) return;
  material.userData.coat = coat;
  const recipe = RECIPES[coat];
  material.userData.coatUniforms = {
    uCoatLight: { value: new THREE.Color(recipe.light) },
    uCoatDark: { value: new THREE.Color(recipe.dark) },
  };
  // Une copie du matériau porte ses propres couleurs (`recolorMaterial`) : on lit celles du matériau compilé.
  material.onBeforeCompile = function (this: THREE.Material, shader) {
    Object.assign(shader.uniforms, this.userData.coatUniforms);
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>\n${COAT_VERTEX_HEAD}`)
      .replace("#include <begin_vertex>", COAT_VERTEX_BODY);
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>\n${COAT_FRAGMENT_HEAD}\nfloat coatPattern(vec3 p, vec4 region) {\n${recipe.pattern}\n}`)
      .replace("#include <color_fragment>", COAT_FRAGMENT_BODY);
  };
  material.customProgramCacheKey = () => `coat-${coat}`;
  material.needsUpdate = true;
}

const COAT_LABELS: Record<DecorCoat, [string, string]> = {
  zebra: ["Fond", "Rayures"],
  giraffe: ["Fond", "Taches"],
  tiger: ["Fond", "Rayures"],
};

/** Les deux couleurs d'un pelage, et les couleurs peintes qu'il remplace, pour en faire des parties à recolorer. */
export function coatColors(coat: DecorCoat): { light: string; dark: string; painted: string[]; labels: [string, string] } {
  const recipe = RECIPES[coat];
  return { light: recipe.light, dark: recipe.dark, painted: recipe.painted, labels: COAT_LABELS[coat] };
}
